import { useEffect, useMemo, useState } from 'react';

import { toast } from 'sonner';

import { ColumnsField } from './fields/columns-field';
import { DocOptionsField } from './fields/doc-options-field';
import { FilenameField } from './fields/filename-field';
import { FormatField } from './fields/format-field';
import {
  LARGE_EXPORT_BLOCK_THRESHOLD,
  LARGE_EXPORT_WARNING_THRESHOLD,
  LargeExportBlock,
  LargeExportWarning,
} from './fields/large-export-warning';
import { SourceDocsField } from './fields/source-docs-field';
import { StyleField } from './fields/style-field';
import { ZipContentField } from './fields/zip-content-field';
import type {
  DownloadDefinition,
  DownloadFormValues,
  DownloadTrackingPayload,
  DownloadVariant,
  ZipOption,
} from './types';
import { defaultDocOptions, defaultIconForKind, isBundleVariant, kindShowsFilename } from './types';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { BundleContent, type BundleDraftState } from '@/new/components/dialogs/structured-export-modal/bundle-content';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  definition: DownloadDefinition;
  onAsyncStarted?: (payload: {
    variantId: string;
    exportId: string;
    jobId?: string;
    format: DownloadFormValues['format'];
    withSourceDocuments?: boolean;
    bundleName?: string;
    filename?: string;
  }) => void;
}

interface VariantState {
  filename: string;
  format: string;
  columns: string[];
  relativityColumns: string[];
  style: string;
  docOptions: ReturnType<typeof defaultDocOptions>;
  includeSourceDocuments: boolean;
  orderByDate: boolean;
  zipContent: ZipOption;
}

function makeInitialState(variant: DownloadVariant): VariantState {
  if (variant.kind === 'bundle') {
    return {
      filename: '',
      format: '',
      columns: [],
      relativityColumns: [],
      style: '',
      docOptions: defaultDocOptions(),
      includeSourceDocuments: false,
      orderByDate: false,
      zipContent: 'original',
    };
  }
  const firstFormat = variant.formats[0];
  return {
    filename: variant.defaultFilename ?? '',
    format: firstFormat ? String(firstFormat) : '',
    columns: variant.columns?.default ?? [],
    relativityColumns: [],
    style: '',
    docOptions: defaultDocOptions(),
    includeSourceDocuments: false,
    orderByDate: false,
    zipContent: 'original',
  };
}

export const DownloadModal = ({ open, onOpenChange, definition, onAsyncStarted }: Props) => {
  const { variants, title = 'Download', scopes, defaultScopeId } = definition;
  const [selectedScopeId, setSelectedScopeId] = useState(defaultScopeId ?? 'all');
  const [selectedVariantId, setSelectedVariantId] = useState<string>(
    () => definition.defaultVariantId ?? variants[0]?.id ?? '',
  );
  const [stateByVariant, setStateByVariant] = useState<Record<string, VariantState>>(() => {
    const initial: Record<string, VariantState> = {};
    for (const v of variants) initial[v.id] = makeInitialState(v);
    return initial;
  });
  const [largeExportAcknowledged, setLargeExportAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<'variant' | 'form'>(variants.length > 1 ? 'variant' : 'form');
  const [bundleStateByVariant, setBundleStateByVariant] = useState<Record<string, BundleDraftState | undefined>>({});

  const variant = useMemo(
    () => variants.find((v) => v.id === selectedVariantId) ?? variants[0],
    [variants, selectedVariantId],
  );

  useEffect(() => {
    if (!open) {
      setLargeExportAcknowledged(false);
      setIsSubmitting(false);
      const reset: Record<string, VariantState> = {};
      for (const v of variants) reset[v.id] = makeInitialState(v);
      setStateByVariant(reset);
      setSelectedVariantId(definition.defaultVariantId ?? variants[0]?.id ?? '');
      setSelectedScopeId(defaultScopeId ?? 'all');
      setStep(variants.length > 1 ? 'variant' : 'form');
      setBundleStateByVariant({});
    }
  }, [open, variants, definition.defaultVariantId, defaultScopeId]);

  if (!variant) return null;

  const state = stateByVariant[variant.id] ?? makeInitialState(variant);
  const patch = (next: Partial<VariantState>) =>
    setStateByVariant((prev) => ({
      ...prev,
      [variant.id]: { ...(prev[variant.id] ?? makeInitialState(variant)), ...next },
    }));

  const dv = variant.kind !== 'bundle' ? variant : null;

  const showFilename = dv !== null && kindShowsFilename(dv.kind);
  const showFormat = dv !== null && dv.formats.length > 1;
  const selectedScope = scopes?.find((s) => s.id === selectedScopeId);
  const warningTotalCount = scopes ? selectedScope?.totalCount : definition.totalCount;
  const hasLargeExportNotice =
    dv !== null &&
    dv.largeExportWarning === true &&
    warningTotalCount !== undefined &&
    warningTotalCount > LARGE_EXPORT_WARNING_THRESHOLD;
  const showLargeExportBlock =
    dv !== null &&
    dv.largeExportWarning === true &&
    warningTotalCount !== undefined &&
    warningTotalCount > LARGE_EXPORT_BLOCK_THRESHOLD;
  const showLargeExportWarning = hasLargeExportNotice && !showLargeExportBlock;
  const isVariantStep = step === 'variant';

  const disabledReason = showLargeExportBlock
    ? 'This export is too large to download. Reduce the number of items before continuing.'
    : showLargeExportWarning && !largeExportAcknowledged
      ? 'Please acknowledge that this export may take a while before continuing.'
      : undefined;

  const isBundleFlow = !dv && step === 'form';

  const handleDownload = async () => {
    if (disabledReason || !dv) return;
    const values: DownloadFormValues = {
      filename: state.filename,
      format: state.format as DownloadFormValues['format'],
      scope: scopes ? selectedScopeId : undefined,
      columns: dv.columns ? state.columns : undefined,
      relativityColumns:
        dv.columns?.relativity && state.relativityColumns.length > 0 ? state.relativityColumns : undefined,
      style: dv.style ? state.style : undefined,
      docOptions: dv.docOptions ? state.docOptions : undefined,
      includeSourceDocuments: dv.sourceDocs ? state.includeSourceDocuments : undefined,
      orderByDate: dv.sourceDocs && state.includeSourceDocuments ? state.orderByDate : undefined,
      zipContent: dv.showZipContent ? state.zipContent : undefined,
    };

    if (definition.tracking) {
      const payload: DownloadTrackingPayload = {
        ...definition.tracking.basePayload,
        variant: dv.kind,
        downloadFormat: values.format,
        scope: values.scope,
        withSourceDocuments: values.includeSourceDocuments,
        zipContent: values.zipContent,
      };
      trackCustomEvent(definition.tracking.event, payload);
    }

    setIsSubmitting(true);
    try {
      if (dv.mode === 'async') {
        const { exportId, jobId } = await dv.mutate(values);
        onOpenChange(false);
        onAsyncStarted?.({
          variantId: dv.id,
          exportId,
          jobId,
          format: values.format,
          withSourceDocuments: values.includeSourceDocuments,
          filename: values.filename,
        });
      } else {
        await dv.mutate(values);
        onOpenChange(false);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Download failed';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'transition-none',
          isBundleFlow
            ? 'flex max-h-[90vh] w-[96vw] max-w-[1600px] flex-col overflow-hidden'
            : isVariantStep
              ? 'flex max-h-[90vh] w-fit max-w-[calc(100vw-2rem)] flex-col overflow-hidden'
              : 'flex max-h-[90vh] flex-col overflow-hidden',
        )}
      >
        <DialogHeader className={isBundleFlow ? 'sr-only' : undefined}>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {isBundleVariant(variant) && step !== 'variant' ? (
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-hidden">
            <BundleContent
              variantId={variant.id}
              config={variant.bundle}
              tracking={variant.tracking ?? definition.tracking}
              onClose={() => onOpenChange(false)}
              onBack={variants.length > 1 ? () => setStep('variant') : undefined}
              onAsyncStarted={onAsyncStarted}
              draftState={bundleStateByVariant[variant.id]}
              onDraftStateChange={(draft) =>
                setBundleStateByVariant((prev) => ({
                  ...prev,
                  [variant.id]: draft,
                }))
              }
            />
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-hidden">
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="flex flex-col gap-4 px-1">
                {step === 'form' &&
                  scopes &&
                  scopes.length > 0 &&
                  (() => {
                    const hasMultiple = scopes.length > 1;
                    const label = scopes.find((scope) => scope.id === selectedScopeId)?.label ?? scopes[0]?.label;
                    return hasMultiple ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="group flex w-full cursor-pointer items-center gap-2 rounded-md border border-blue-100 bg-blue-50/50 px-3 py-2 text-sm text-blue-700 transition-colors hover:bg-blue-100/50"
                          >
                            <Icon className="lucide--layers text-blue-400" />
                            <span className="flex-1 text-left">{label}</span>
                            <Icon className="lucide--chevron-up text-blue-400 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          side="top"
                          align="start"
                          className="w-[var(--radix-dropdown-menu-trigger-width)]"
                        >
                          <DropdownMenuRadioGroup value={selectedScopeId} onValueChange={setSelectedScopeId}>
                            {scopes.map((scope) => (
                              <DropdownMenuRadioItem key={scope.id} value={scope.id}>
                                {scope.label}
                              </DropdownMenuRadioItem>
                            ))}
                          </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <div className="flex w-full items-center gap-2 rounded-md border border-gray-200 bg-gray-50/50 px-3 py-2 text-sm text-gray-400">
                        <Icon className="lucide--layers" />
                        <span className="flex-1 text-left">{label}</span>
                      </div>
                    );
                  })()}

                {isVariantStep ? (
                  <div className="flex flex-row flex-wrap justify-center gap-4">
                    {variants.map((v) => {
                      const variantDisabledReason = isBundleVariant(v) ? v.disabledReason : undefined;
                      const isDisabled = !!variantDisabledReason;
                      const tile = (
                        <button
                          key={v.id}
                          type="button"
                          disabled={isDisabled}
                          onClick={() => {
                            if (isDisabled) return;
                            setSelectedVariantId(v.id);
                            setStep('form');
                          }}
                          className={
                            isDisabled
                              ? 'flex w-40 shrink-0 cursor-not-allowed flex-col items-center justify-start rounded-lg border border-gray-200 bg-gray-50/50 p-4 opacity-50'
                              : 'flex w-40 shrink-0 cursor-pointer flex-col items-center justify-start rounded-lg border border-gray-200 bg-gray-50/50 p-4 transition-all duration-200 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50'
                          }
                        >
                          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-blue-50 to-blue-100/50 text-lg text-blue-600">
                            <Icon className={v.icon ?? defaultIconForKind(v.kind)} />
                          </div>
                          <span className="mb-0.5 text-base font-medium text-gray-700">{v.label}</span>
                          {v.description && <span className="text-center text-xs text-gray-400">{v.description}</span>}
                        </button>
                      );
                      if (!isDisabled) return tile;
                      return (
                        <Tooltip key={v.id}>
                          <TooltipTrigger asChild>
                            <span>{tile}</span>
                          </TooltipTrigger>
                          <TooltipContent>{variantDisabledReason}</TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    {showLargeExportBlock && warningTotalCount !== undefined && (
                      <LargeExportBlock totalCount={warningTotalCount} />
                    )}

                    {showLargeExportWarning && warningTotalCount !== undefined && (
                      <LargeExportWarning
                        totalCount={warningTotalCount}
                        acknowledged={largeExportAcknowledged}
                        onAcknowledgedChange={setLargeExportAcknowledged}
                      />
                    )}

                    <div className="flex flex-col gap-4">
                      {showFilename && (
                        <FilenameField value={state.filename} onChange={(filename) => patch({ filename })} />
                      )}

                      {dv?.style && dv.styleItemNoun && (
                        <StyleField
                          value={state.style}
                          onChange={(style) => patch({ style })}
                          itemNoun={dv.styleItemNoun}
                        />
                      )}

                      {dv?.columns && (
                        <ColumnsField
                          spec={dv.columns}
                          columns={state.columns}
                          onColumnsChange={(columns) => patch({ columns })}
                          relativityColumns={state.relativityColumns}
                          onRelativityColumnsChange={(relativityColumns) => patch({ relativityColumns })}
                        />
                      )}

                      {showFormat && dv && (
                        <FormatField
                          value={state.format as DownloadFormValues['format']}
                          onChange={(format) => patch({ format: String(format) })}
                          options={dv.formats}
                        />
                      )}

                      {dv?.docOptions && (
                        <DocOptionsField value={state.docOptions} onChange={(docOptions) => patch({ docOptions })} />
                      )}

                      {dv?.sourceDocs && (
                        <SourceDocsField
                          includeSourceDocuments={state.includeSourceDocuments}
                          orderByDate={state.orderByDate}
                          onIncludeSourceDocumentsChange={(v) =>
                            patch({ includeSourceDocuments: v, orderByDate: v ? state.orderByDate : false })
                          }
                          onOrderByDateChange={(v) => patch({ orderByDate: v })}
                          showEmailNotification
                        />
                      )}

                      {dv?.showZipContent && (
                        <ZipContentField value={state.zipContent} onChange={(v) => patch({ zipContent: v })} />
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {dv && step === 'form' && (
              <DialogFooter className="gap-2 p-1">
                {variants.length > 1 && (
                  <Button variant="outline" onClick={() => setStep('variant')} disabled={isSubmitting}>
                    <Icon className="lucide--arrow-left" />
                    Back
                  </Button>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex-1">
                      <Button
                        variant="primary"
                        className="w-full"
                        onClick={handleDownload}
                        disabled={!!disabledReason || isSubmitting}
                      >
                        {isSubmitting ? 'Preparing...' : 'Download'}
                        <Icon className="lucide--download" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {disabledReason && <TooltipContent>{disabledReason}</TooltipContent>}
                </Tooltip>
              </DialogFooter>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
